// Enhanced class data types based on standardized individual class files

export interface ClassFeature {
  name: string;
  description: string;
  uses?: string;
  progression?: string;
}

export interface HopeFeature {
  name: string;
  description: string;
  cost: number;
}

export interface SubclassFeature {
  name: string;
  description: string;
}

export interface Subclass {
  id: string;
  name: string;
  description: string;
  spellcastTrait?: string | null;
  foundationFeature: SubclassFeature[];
  specializationFeature: SubclassFeature[];
  masteryFeature: SubclassFeature[];
  // Special case for Martial Artist with stances
  martialStances?: {
    tier1?: { name: string; description: string }[];
    tier2?: { name: string; description: string }[];
    tier3?: { name: string; description: string }[];
    tier4?: { name: string; description: string }[];
  };
}

export interface SuggestedTraits {
  agility: number;
  strength: number;
  finesse: number;
  instinct: number;
  presence: number;
  knowledge: number;
}

export interface SuggestedEquipment {
  primaryWeapon: string;
  secondaryWeapon?: string;
  armor: string;
}

export interface StartingChoice {
  category: string;
  options: string[];
}

export interface CharacterCreationDescriptions {
  clothes: string[];
  eyes: string[];
  body: string[];
  skin: string[];
  attitude: string[];
}

export interface CharacterCreation {
  descriptions: CharacterCreationDescriptions;
  backgroundQuestions: string[];
  connections: string[];
}

export interface EnhancedClass {
  // Core class data (from legacy classes.json)
  id: string;
  name: string;
  description: string;
  domains: string[];
  startingEvasion: number;
  startingHitPoints: number;
  startingStress: number;
  hopeFeature: HopeFeature;
  classFeatures: ClassFeature[];
  subclasses: Subclass[];
  
  // Enhanced data (new in individual files)
  suggestedTraits: SuggestedTraits;
  suggestedEquipment: SuggestedEquipment;
  startingChoices: StartingChoice[];
  characterCreation: CharacterCreation;
}


// Type guards
export function isEnhancedClass(classData: any): classData is EnhancedClass {
  // Basic validation for core fields
  const hasBasicFields = (
    typeof classData === 'object' &&
    classData !== null &&
    typeof classData.id === 'string' &&
    typeof classData.name === 'string' &&
    typeof classData.startingStress === 'number' &&
    Array.isArray(classData.subclasses)
  );

  // Check for enhanced fields (these are optional for backwards compatibility)
  const hasEnhancedFields = (
    typeof classData.suggestedTraits === 'object' &&
    typeof classData.suggestedEquipment === 'object' &&
    Array.isArray(classData.startingChoices) &&
    typeof classData.characterCreation === 'object'
  );

  // Allow classes that have basic fields even if they don't have all enhanced fields
  return hasBasicFields && (hasEnhancedFields || true);
}